import { useEffect, useState } from 'react'
import { useAuth } from '@context/AuthContext'
import { APP_CONFIG } from '@config/constants'
import PortalBrand from './PortalBrand'
import './portalPassword.css'

const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'Pelo menos 8 caracteres', test: (value) => value.length >= 8 },
  { id: 'uppercase', label: 'Uma letra maiúscula', test: (value) => /\p{Lu}/u.test(value) },
  { id: 'number', label: 'Um número', test: (value) => /\p{N}/u.test(value) },
  { id: 'special', label: 'Um caractere especial (ex.: ! @ #)', test: (value) => /[^\p{L}\p{N}\s]/u.test(value) },
]

function getPasswordStrength(password) {
  const completedRequirements = PASSWORD_REQUIREMENTS.filter((requirement) => requirement.test(password)).length

  if (!password) return { completedRequirements, label: 'Digite uma senha', tone: 'empty' }
  if (completedRequirements === PASSWORD_REQUIREMENTS.length) return { completedRequirements, label: 'Senha forte', tone: 'strong' }
  if (completedRequirements >= 3) return { completedRequirements, label: 'Quase pronta', tone: 'medium' }
  return { completedRequirements, label: 'Senha fraca', tone: 'weak' }
}

export default function PortalPasswordGate({ children }) {
  const { user, changePassword, logout } = useAuth()
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const isExternalClient = String(user?.role || '').trim().toUpperCase() === 'CLIENT'
  const passwordStrength = getPasswordStrength(password)
  const isPasswordValid = passwordStrength.completedRequirements === PASSWORD_REQUIREMENTS.length

  useEffect(() => {
    if (!isExternalClient || !user?.forcePasswordChange) return undefined

    const previousTitle = document.title
    document.title = `${APP_CONFIG.PORTAL_APP_NAME} | Veiga Advogados`
    return () => { document.title = previousTitle }
  }, [isExternalClient, user?.forcePasswordChange])

  if (!isExternalClient || !user?.forcePasswordChange) return children

  const submit = async (event) => {
    event.preventDefault()
    setError('')
    if (password !== confirmation) {
      setError('As senhas não coincidem.')
      return
    }
    if (!isPasswordValid) {
      setError('A senha ainda não atende aos requisitos de segurança informados.')
      return
    }

    setSaving(true)
    const result = await changePassword({ newPassword: password, confirmPassword: confirmation })
    setSaving(false)
    if (!result.success) setError(result.error || 'Não foi possível definir a nova senha.')
  }

  return (
    <main className="client-portal portal-centered-page">
      <div className="portal-auth-card portal-denied-card portal-password-setup">
        <PortalBrand className="portal-password-brand" />
        <span className="portal-eyebrow">Primeiro acesso</span>
        <h1>Crie sua nova senha</h1>
        <p>A senha fornecida pela equipe Veiga é temporária. Defina uma senha pessoal antes de acessar os dados da sua empresa.</p>
        <form className="portal-login-form" onSubmit={submit}>
          <label className="portal-field">
            <span>Nova senha</span>
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" required minLength={8} />
          </label>
          <div className={`portal-password-strength is-${passwordStrength.tone}`} aria-live="polite">
            <div className="portal-password-strength-heading">
              <span>Força da senha</span>
              <strong>{passwordStrength.label}</strong>
            </div>
            <div className="portal-password-strength-meter" role="progressbar" aria-label="Força da senha" aria-valuemin="0" aria-valuemax={PASSWORD_REQUIREMENTS.length} aria-valuenow={passwordStrength.completedRequirements}>
              {PASSWORD_REQUIREMENTS.map((requirement) => <span key={requirement.id} className={requirement.test(password) ? 'is-complete' : ''} />)}
            </div>
            <ul className="portal-password-requirements">
              {PASSWORD_REQUIREMENTS.map((requirement) => {
                const isComplete = requirement.test(password)
                return <li key={requirement.id} className={isComplete ? 'is-complete' : ''}><i className={`fas ${isComplete ? 'fa-circle-check' : 'fa-circle'}`} /> {requirement.label}</li>
              })}
            </ul>
          </div>
          <label className="portal-field">
            <span>Confirmar nova senha</span>
            <input type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" required minLength={8} />
          </label>
          {error ? <div className="portal-form-error"><i className="fas fa-circle-exclamation" /> {error}</div> : null}
          <button type="submit" className="portal-primary-button" disabled={saving}>{saving ? 'Salvando...' : 'Definir senha e continuar'}</button>
        </form>
        <button type="button" className="portal-link-button" onClick={logout}>Sair</button>
      </div>
    </main>
  )
}
